import { ApiError } from './api'
import { hmrcAuthService } from './hmrcAuthService'

const HMRC_TOKENS_KEY = 'hmrcTokens'
const HMRC_AUTH_STATUS_KEY = 'hmrcAuthStatus'

/**
 * Clear any legacy HMRC auth data stored locally.
 */
export const clearHmrcTokens = (): void => {
  try {
    localStorage.removeItem(HMRC_TOKENS_KEY)
    localStorage.removeItem(HMRC_AUTH_STATUS_KEY)
  } catch (error) {
    console.error('Failed to clear HMRC auth data:', error)
  }
}

/**
 * Check if user is HMRC authorized by verifying a refresh token exists on the backend.
 * If a refresh token is available, the user has completed HMRC authorization.
 * Optionally attempts to refresh the token, but a refresh failure does NOT
 * mean the user is unauthorized (the token may simply not need refreshing yet).
 */
export const isHmrcAuthorized = async (
  companyId?: string
): Promise<boolean> => {
  try {
    const tokenEnvelope = await hmrcAuthService.getRefreshToken()
    const tokenResponse = tokenEnvelope?.response as any

    // Check multiple possible response shapes for the refresh token
    const refreshToken =
      tokenResponse?.refreshToken ||
      tokenResponse?.data?.refreshToken ||
      tokenResponse?.response?.refreshToken

    // If the backend returned a token, user is authorized
    if (refreshToken && typeof refreshToken === 'string') {
      // Attempt to refresh in background (non-blocking) - don't gate auth on this
      try {
        const payload = companyId
          ? { refreshToken, companyId }
          : { refreshToken }
        await hmrcAuthService.refreshTokens(payload)
      } catch {
        // Refresh failed but user still has a valid token - they are authorized
        console.warn('HMRC token refresh failed (non-critical) - user still authorized')
      }
      return true
    }

    // If the GET /token call succeeded (no error thrown) but returned a truthy
    // response with success: true, treat user as authorized even if token shape
    // doesn't match our extraction (backend may have changed its schema).
    if (tokenResponse?.success === true) {
      return true
    }

    return false
  } catch (error) {
    if (error instanceof ApiError) {
      return false
    }
    return false
  }
}
